import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { staffValidationScheme } from './staffValidation';
import { useNavigation, useNavigationContext, useProvider } from '~/Hooks';
import { selectLoading } from '~/store/app';
import { ExtendedStaff } from './ExtendedStaff.model';
import { Route } from '~/Constants/Routes.constants';
import { IItemCreationSectionInjectedProps } from '~/ModularComponents';
import {
  useRequestOrgStaff,
  useRequestOrgStaffCreate,
  useRequestOrgStaffUpdate,
} from '~/requests/providerService/useRequestOrgStaff';
import { UserRole } from '~/Models/DTO/Role.model';
import { routeReplacer } from '~/Helpers/String.helper';

export const useStaffEdit = () => {
  const { t } = useTranslation();
  const { getSearchParams, dangerouslyNavigate, getParams } = useNavigation();
  const { id } = getSearchParams();
  const { currentProvider } = useProvider();
  const { blockNavigation } = useNavigationContext();
  const { domain = '' } = getParams();

  const {
    isLoading: isStaffLoading,
    error: staffError,
    data: currentStaff,
    refetch,
  } = useRequestOrgStaff(id);
  const {
    isLoading: isStaffCreationLoading,
    error: staffCreationError,
    mutate: createStaff,
    isSuccess: isStaffCreatedSuccessfully,
  } = useRequestOrgStaffCreate();
  const {
    isLoading: isStaffUpdateLoading,
    error: staffUpdateError,
    mutate: updateStaff,
    isSuccess: isStaffUpdatedSuccessfully,
  } = useRequestOrgStaffUpdate();
  const imageUploading = useSelector(selectLoading(REQUEST_ACTIONS.UPLOAD_IMAGE));

  // state
  const [initialValues, setInitialValues] = useState<ExtendedStaff>({
    id: '',
    firstName: '',
    lastName: '',
    imageUrl: '',
    email: '',
    roles: [],
    fullAccessRoles: [],
    restrictedRoles: [],
    trainerRoles: [],
    organisationRoles: [],
    isOrganisationManager: false,
  });
  const [fullName, setFullName] = useState<string>('');

  useEffect(() => {
    if (isStaffCreatedSuccessfully || isStaffUpdatedSuccessfully) {
      blockNavigation(false);
      dangerouslyNavigate(routeReplacer(Route.ORGANISATION_STAFF, { domain }));
    }
  }, [isStaffCreatedSuccessfully, isStaffUpdatedSuccessfully]);

  const setName = (staff: ExtendedStaff) => {
    setFullName(`${staff?.firstName} ${staff?.lastName}`);
  };
  const onSave = useCallback(
    async (store: IItemCreationSectionInjectedProps<ExtendedStaff>) => {
      const payload = store.values;
      payload.roles = payload.roles.filter(
        (role) => role.accessType !== UserRole.ORGANISATION_MANAGER,
      );
      if (payload.isOrganisationManager) {
        const orgRole = {
          resources: [
            {
              id: currentProvider?.organisationId || '',
              name: currentProvider?.name || '',
              resourceType: 'ORGANISATION',
            },
          ],
          accessType: UserRole.ORGANISATION_MANAGER,
        };
        payload.roles.push(orgRole);
      }
      if (id) {
        updateStaff(payload);
      } else {
        createStaff(payload);
      }
    },
    [id, currentProvider, initialValues],
  );

  const validate = useCallback((validateStaff: ExtendedStaff) => setName(validateStaff), [setName]);

  useEffect(() => {
    if (currentStaff) {
      const extendedStaff: ExtendedStaff = {
        ...currentStaff,
        fullAccessRoles:
          currentStaff.roles.filter((role) => role.accessType === UserRole.FULL_ACCESS)?.[0]
            ?.resources || [],
        restrictedRoles:
          currentStaff.roles.filter((role) => role.accessType === UserRole.RESTRICTED)?.[0]
            ?.resources || [],
        trainerRoles:
          currentStaff.roles.filter((role) => role.accessType === UserRole.TRAINER)?.[0]
            ?.resources || [],
        organisationRoles:
          currentStaff.roles.filter(
            (role) => role.accessType === UserRole.ORGANISATION_MANAGER,
          )?.[0]?.resources || [],
        isOrganisationManager:
          currentStaff.roles.filter(
            (role) => role.accessType === UserRole.ORGANISATION_MANAGER,
          )?.[0]?.resources?.length > 0,
      };
      setInitialValues(extendedStaff);
    }
  }, [currentStaff]);

  // workaround since the query invalidation is not working correctly.
  useEffect(() => {
    refetch();
  }, []);

  return {
    id,
    onSave,
    initialValues,
    validate,
    fullName,
    error: staffError || staffCreationError || staffUpdateError || {},
    isLoading: isStaffLoading || isStaffCreationLoading || isStaffUpdateLoading || imageUploading,
    validationSchema: staffValidationScheme(t),
  };
};
